/**
 * Read helpers for the list screens (M4 F13 / F20): the per-source ledger roll-up
 * (`GET /analysis-runs/summary`, M2 R18) and the group membership of several sources
 * (`GET /analysis-groups/membership`, contract 8). Both return plain maps keyed by the source id
 * as a string so table rows can look their entry up without caring about id types.
 */
import http from '@/http-common';
import AnalysisGroupsService from '@/services/AnalysisGroupsService';

const MAX_IDS_PER_REQUEST = 200;

function uniqueIds(sourceIds) {
  return Array.from(
    new Set(
      (Array.isArray(sourceIds) ? sourceIds : [])
        .map((id) => (id == null ? '' : String(id).trim()))
        .filter((id) => id !== '')
    )
  );
}

function chunk(list, size) {
  const out = [];
  for (let index = 0; index < list.length; index += size) out.push(list.slice(index, index + size));
  return out;
}

function itemsOf(response) {
  const body = response?.data?.data ?? response?.data ?? {};
  if (Array.isArray(body)) return body;
  return Array.isArray(body.items) ? body.items : [];
}

/**
 * `{ [source_id]: { active_run_id, active_run_pinned, attempts, last_run, runs_by_group } }`.
 * Sources without a ledger entry are absent. Never rejects: a failed page yields no entries.
 * @param {'interview'|'manual_analysis'} sourceType
 * @param {Array<number|string>} sourceIds
 */
export async function fetchSourceRunSummaries(sourceType, sourceIds) {
  const ids = uniqueIds(sourceIds);
  const out = {};
  if (!ids.length) return out;
  await Promise.all(
    chunk(ids, MAX_IDS_PER_REQUEST).map(async (page) => {
      try {
        const response = await http.post('analysis-runs/summary', {
          source_type: sourceType,
          source_ids: page,
        });
        itemsOf(response).forEach((item) => {
          if (item && item.source_id != null) out[String(item.source_id)] = item;
        });
      } catch (error) {
        // The list keeps working without ledger chips; the row falls back to the SQL status.
      }
    })
  );
  return out;
}

/**
 * `{ [source_id]: [{ group_id, name, member_id, pinned_run_id, frozen, resolution }] }`.
 * Never rejects (an unavailable groups API yields an empty map).
 * @param {'interview'|'manual_analysis'} sourceType
 * @param {Array<number|string>} sourceIds
 */
export async function fetchSourceMemberships(sourceType, sourceIds) {
  const ids = uniqueIds(sourceIds);
  const out = {};
  if (!ids.length) return out;
  await Promise.all(
    chunk(ids, MAX_IDS_PER_REQUEST).map(async (page) => {
      try {
        const response = await AnalysisGroupsService.membership(sourceType, page);
        itemsOf(response).forEach((item) => {
          if (item && item.source_id != null) {
            out[String(item.source_id)] = Array.isArray(item.groups) ? item.groups : [];
          }
        });
      } catch (error) {
        // Membership chips are optional; the table renders without them.
      }
    })
  );
  return out;
}
